import React, { useEffect, useState } from 'react';
import {
  getRoomListings, setRoomListingStatus, getRoomHosts, setRoomHostStatus,
  getRoomBookings, cancelRoomBookingAsAdmin, getRoomAuditLog, getRoomConfig, setRoomConfigValue,
} from '../api/admin';

// Admin & Cancellation Spec — Admin Dashboard, all 5 sections:
// Listing Moderation, Host Verification, Booking Oversight, Audit Log,
// Configuration. Plain hooks + inline styles (matching the simpler pattern
// used across the public-website side of this project) rather than this
// app's react-query/CSS-module convention, to move quickly — functionally
// equivalent, same backend, no different data contract.
const TABS = ['Listing Moderation', 'Host Verification', 'Booking Oversight', 'Audit Log', 'Configuration'];

const RoomsAdmin = () => {
  const [tab, setTab] = useState(TABS[0]);
  return (
    <div style={s.page}>
      <h1 style={s.h1}>Rooms & Marketplace — Admin</h1>
      <div style={s.tabs}>
        {TABS.map((t) => (
          <button key={t} style={{ ...s.tab, ...(tab === t ? s.tabActive : {}) }} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>
      {tab === 'Listing Moderation' && <ListingModeration />}
      {tab === 'Host Verification' && <HostVerification />}
      {tab === 'Booking Oversight' && <BookingOversight />}
      {tab === 'Audit Log' && <AuditLog />}
      {tab === 'Configuration' && <Configuration />}
    </div>
  );
};

// ─── 1. Listing Moderation ────────────────────────────────────────────────────
function ListingModeration() {
  const [listings, setListings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = () => {
    setLoading(true);
    getRoomListings(statusFilter ? { status: statusFilter } : {}).then((res) => setListings(res.data?.listings || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const act = async (id, status, rejection_reason) => {
    await setRoomListingStatus(id, status, rejection_reason);
    setRejectingId(null);
    setRejectReason('');
    load();
  };

  return (
    <div>
      <select style={s.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
        <option value="">All statuses</option>
        <option value="pending">Pending</option>
        <option value="approved">Approved</option>
        <option value="rejected">Rejected</option>
        <option value="suspended">Suspended</option>
      </select>
      {loading ? <p>Loading…</p> : (
        <table style={s.table}>
          <thead><tr><th style={s.th}>Title</th><th style={s.th}>Host</th><th style={s.th}>Status</th><th style={s.th}>Country</th><th style={s.th}>Actions</th></tr></thead>
          <tbody>
            {listings.map((l) => (
              <tr key={l.id}>
                <td style={s.td}>{l.title}</td>
                <td style={s.td}>{l.host?.full_name || l.host?.email}</td>
                <td style={s.td}><span style={s.badge}>{l.status}</span></td>
                <td style={s.td}>{l.country || '—'}</td>
                <td style={s.td}>
                  {l.status !== 'approved' && <button style={s.btnGreen} onClick={() => act(l.id, 'approved')}>Approve</button>}
                  {l.status !== 'suspended' && l.status === 'approved' && <button style={s.btnGray} onClick={() => act(l.id, 'suspended')}>Suspend</button>}
                  {l.status !== 'rejected' && (
                    rejectingId === l.id ? (
                      <span>
                        <input style={s.smallInput} placeholder="Reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                        <button style={s.btnRed} onClick={() => act(l.id, 'rejected', rejectReason)}>Confirm reject</button>
                      </span>
                    ) : <button style={s.btnRed} onClick={() => setRejectingId(l.id)}>Reject</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── 2. Host Verification ─────────────────────────────────────────────────────
function HostVerification() {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockingId, setBlockingId] = useState(null);
  const [blockReason, setBlockReason] = useState('');

  const load = () => { setLoading(true); getRoomHosts().then((res) => setHosts(res.data?.hosts || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const act = async (id, kyc_status, blocked_reason) => {
    await setRoomHostStatus(id, kyc_status, blocked_reason);
    setBlockingId(null); setBlockReason('');
    load();
  };

  return loading ? <p>Loading…</p> : (
    <table style={s.table}>
      <thead><tr><th style={s.th}>Host</th><th style={s.th}>KYC status</th><th style={s.th}>Actions</th></tr></thead>
      <tbody>
        {hosts.map((h) => (
          <tr key={h.id}>
            <td style={s.td}>{h.user?.full_name || h.user?.email}</td>
            <td style={s.td}><span style={s.badge}>{h.kyc_status}</span></td>
            <td style={s.td}>
              {h.kyc_status !== 'verified' && <button style={s.btnGreen} onClick={() => act(h.id, 'verified')}>Verify</button>}
              {h.kyc_status !== 'blocked' ? (
                blockingId === h.id ? (
                  <span>
                    <input style={s.smallInput} placeholder="Reason" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} />
                    <button style={s.btnRed} onClick={() => act(h.id, 'blocked', blockReason)}>Confirm block</button>
                  </span>
                ) : <button style={s.btnRed} onClick={() => setBlockingId(h.id)}>Block</button>
              ) : <button style={s.btnGray} onClick={() => act(h.id, 'pending')}>Unblock</button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── 3. Booking Oversight ─────────────────────────────────────────────────────
function BookingOversight() {
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [note, setNote] = useState('');

  const load = () => { setLoading(true); getRoomBookings(statusFilter ? { status: statusFilter } : {}).then((res) => setBookings(res.data?.bookings || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const doCancel = async (id) => {
    if (!note.trim()) { alert('A reason/note is required for an admin cancellation.'); return; }
    await cancelRoomBookingAsAdmin(id, refundAmount === '' ? undefined : Math.round(Number(refundAmount) * 100), note);
    setCancellingId(null); setRefundAmount(''); setNote('');
    load();
  };

  return (
    <div>
      <select style={s.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
        <option value="">All statuses</option>
        <option value="pending">Pending</option>
        <option value="confirmed">Confirmed</option>
        <option value="checked_in">Checked in</option>
        <option value="completed">Completed</option>
        <option value="cancelled">Cancelled</option>
      </select>
      {loading ? <p>Loading…</p> : (
        <table style={s.table}>
          <thead><tr><th style={s.th}>Listing</th><th style={s.th}>Guest</th><th style={s.th}>Dates</th><th style={s.th}>Total</th><th style={s.th}>Status</th><th style={s.th}>Actions</th></tr></thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id}>
                <td style={s.td}>{b.listing?.title}</td>
                <td style={s.td}>{b.guest?.full_name || b.guest?.email}</td>
                <td style={s.td}>{b.check_in} → {b.check_out}</td>
                <td style={s.td}>${(b.total_price_cents / 100).toFixed(2)}</td>
                <td style={s.td}><span style={s.badge}>{b.status}</span></td>
                <td style={s.td}>
                  {['pending', 'confirmed'].includes(b.status) && (
                    cancellingId === b.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <input style={s.smallInput} placeholder="Refund $ (blank = auto)" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} />
                        <input style={s.smallInput} placeholder="Reason (required)" value={note} onChange={(e) => setNote(e.target.value)} />
                        <button style={s.btnRed} onClick={() => doCancel(b.id)}>Confirm cancel</button>
                      </div>
                    ) : <button style={s.btnRed} onClick={() => setCancellingId(b.id)}>Cancel + refund</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── 4. Audit Log ─────────────────────────────────────────────────────────────
function AuditLog() {
  const [events, setEvents] = useState([]);
  const [actionFilter, setActionFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); getRoomAuditLog(actionFilter ? { action: actionFilter } : {}).then((res) => setEvents(res.data?.events || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input style={s.select} placeholder="Filter by action (e.g. listing.)" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} />
        <button style={s.btnGray} onClick={load}>Filter</button>
        <a href={`${process.env.REACT_APP_API_URL || ''}/api/rooms/admin/audit-log?format=csv`} target="_blank" rel="noreferrer" style={s.btnGray}>Export CSV</a>
      </div>
      {loading ? <p>Loading…</p> : (
        <table style={s.table}>
          <thead><tr><th style={s.th}>When</th><th style={s.th}>Actor</th><th style={s.th}>Action</th><th style={s.th}>Target</th><th style={s.th}>Reason</th></tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td style={s.td}>{new Date(e.occurred_at).toLocaleString()}</td>
                <td style={s.td}>{e.actor?.full_name || e.actor?.email || e.actor_id}</td>
                <td style={s.td}>{e.action}</td>
                <td style={s.td}>{e.target_id || '—'}</td>
                <td style={s.td}>{e.reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p style={s.hint}>Read-only — this log has no edit/delete action anywhere in the admin UI, and the database itself revokes UPDATE/DELETE on it.</p>
    </div>
  );
}

// ─── 5. Configuration ─────────────────────────────────────────────────────────
function Configuration() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});

  const load = () => { setLoading(true); getRoomConfig().then((res) => setConfig(res.data?.config || {})).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    await setRoomConfigValue(key, editing[key]);
    setEditing((e) => { const n = { ...e }; delete n[key]; return n; });
    load();
  };

  return loading ? <p>Loading…</p> : (
    <table style={s.table}>
      <thead><tr><th style={s.th}>Key</th><th style={s.th}>Value</th><th style={s.th}>Actions</th></tr></thead>
      <tbody>
        {Object.entries(config).map(([key, value]) => (
          <tr key={key}>
            <td style={s.td}>{key}</td>
            <td style={s.td}>
              <input style={s.smallInput} value={editing[key] !== undefined ? editing[key] : value}
                onChange={(e) => setEditing((ed) => ({ ...ed, [key]: e.target.value }))} />
            </td>
            <td style={s.td}>
              {editing[key] !== undefined && editing[key] !== value && <button style={s.btnGreen} onClick={() => save(key)}>Save</button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1100 },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 16 },
  tabs: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
  tab: { padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  tabActive: { background: '#0B6A4A', color: 'white', borderColor: '#0B6A4A' },
  select: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 14 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'white', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#f3f4f6', fontSize: 11, fontWeight: 700 },
  btnGreen: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: 'none', background: '#16a34a', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnRed: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnGray: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: 12, textDecoration: 'none', display: 'inline-block' },
  smallInput: { padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12, marginRight: 4, width: 140 },
  hint: { color: '#9ca3af', fontSize: 12, marginTop: 10 },
};

export default RoomsAdmin;
